import { useDateFns, useNavigation, useProvider } from '~/Hooks';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

import { useEndEventGroup } from '~/Screens/EventGroupList/hooks/useEndEventGroup';
import { useDeleteEventGroup } from '~/Screens/EventGroupList/hooks/useDeleteEventGroup';
import { IErrorResponse } from '~/Models/DTO/Error.models';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { requestReactQuery } from '~/Helpers/ReactQuery.helper';
import { IEvent } from '~/Models/DTO/Event.model';
import { createQueriedURL } from '~/Helpers/String.helper';
import { EVENT_SERVICE_URLS } from '~/Constants/Urls.constants';
import { EventGroup } from '~/Models/DTO/EventGroup.model';
import { ExtendedEventGroup } from '~/Screens/Events/EventCreate/ExtendedEventGroup.model';

export const useArchiveEventGroup = (eventGroup: ExtendedEventGroup) => {
  const { t } = useTranslation();
  const { currentProvider } = useProvider();
  const { convertToProviderTimezone } = useDateFns();
  const { goBack } = useNavigation();
  const {
    endEventGroupOptions,
    onConfirmEndEventGroup,
    onOpenEndEventGroupModal,
    onCloseEndEventGroupModal,
  } = useEndEventGroup({ onSuccess: goBack });

  const {
    deleteEventGroupOptions,
    onConfirmDeleteEventGroup,
    onOpenDeleteEventGroupModal,
    onCloseDeleteEventGroupModal,
  } = useDeleteEventGroup({ onSuccess: goBack });

  const { isFetching: isLoadingNextEvent, data: hasNextEvents } = useQuery<boolean, IErrorResponse>(
    REQUEST_ACTIONS.GET_EVENT_LIST,
    async () =>
      (
        await requestReactQuery<IEvent[]>({
          url: createQueriedURL(EVENT_SERVICE_URLS.EVENT_LIST, {
            providerId: currentProvider?.uuid,
            eventGroupId: eventGroup.id,
            size: 1,
            from: convertToProviderTimezone(new Date().toString()).toISOString(),
          }),
          config: { method: 'GET' },
        })
      ).length > 0,
    {
      enabled: !!eventGroup.id,
      refetchOnMount: true,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );

  const [actionName, onActionClicked] = useMemo(() => {
    if (hasNextEvents) {
      return [
        t('eventGroups.endEventGroup.title'),
        () => onOpenEndEventGroupModal(eventGroup as EventGroup),
      ];
    }
    return [t('app.delete'), () => onOpenDeleteEventGroupModal(eventGroup as EventGroup)];
  }, [eventGroup, hasNextEvents, onOpenEndEventGroupModal, onOpenDeleteEventGroupModal]);

  return {
    actionName,
    onActionClicked,
    isLoadingNextEvent,
    endEventGroupOptions,
    onConfirmEndEventGroup,
    onOpenEndEventGroupModal,
    onCloseEndEventGroupModal,
    deleteEventGroupOptions,
    onConfirmDeleteEventGroup,
    onOpenDeleteEventGroupModal,
    onCloseDeleteEventGroupModal,
  };
};
